<template>
    <div class="map-icons">
        <div style="display: flex; gap: 10px">
            <a-button
                class="map-icon-btn"
                @click="() => showControls = !showControls"
                title="Visualizações"
                aria-label="Visualizações"
            >
                <template #icon>
                    <EyeOutlined v-if="showControls" />
                    <EyeInvisibleOutlined v-else />
                </template>
            </a-button>

            <transition name="fade">
                <div v-if="showControls" class="tabs-container">
                    <div class="save-container">
                        <button 
                            class="map-icon-btn save-btn" 
                            :title="activeConfigId === 'default' ? 'Salvar Visualização Atual' : 'Alterar Visualização Atual'"
                            @click.stop="handleSaveClick"
                        >
                            <i :class="activeConfigId === 'default' ? 'fas fa-plus' : 'fas fa-save'"></i>
                        </button>
                        <transition name="expand-up">
                            <div 
                                v-if="showSaveInput" 
                                class="save-input-container"
                                @click.stop
                            >
                                <div class="input-with-counter">
                                    <input 
                                        v-model="saveConfigName" 
                                        type="text" 
                                        placeholder="Nome da visualização"
                                        class="save-input"
                                        maxlength="10"
                                        @keyup.esc="cancelSave"
                                        ref="saveInputRef"
                                    />
                                    <span class="char-counter">{{ saveConfigName.length }}/10</span>
                                </div>
                                <textarea 
                                    v-model="saveConfigDescription" 
                                    placeholder="Descrição (opcional)"
                                    class="save-input save-textarea"
                                    rows="3"
                                    @keyup.esc="cancelSave"
                                ></textarea>
                                <div class="save-input-buttons">
                                    <button class="save-input-btn save-btn-confirm" @click="saveCurrentConfig">
                                        {{ activeConfigId === 'default' ? 'Salvar' : 'Alterar' }}
                                    </button>
                                    <button class="save-input-btn save-btn-cancel" @click="cancelSave">
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </transition>
                    </div>
                    <div class="tabs-wrapper">
                        <button
                            class="tab-button"
                            :class="{ 'tab-active': activeConfigId === 'default' }"
                            title="Default"
                            @click="applyLayersConfig(defaultConfig, 'default')"
                        >
                            Default
                        </button>
                        <button
                            v-for="config in userLayersConfig" 
                            :key="config.id"
                            class="tab-button"
                            :class="{ 'tab-active': activeConfigId === config.id }"
                            :title="getTabTitle(config)" 
                            @click="applyLayersConfig(config, config.id)"
                            @contextmenu.prevent="showContextMenu($event, config)"
                        >
                            {{ config.name }}
                        </button>
                    </div>
                </div>
            </transition>
        </div>
        
        <div 
            v-if="contextMenu.visible" 
            class="context-menu"
            :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
            @click.stop
            @contextmenu.prevent
        >
            <button class="context-menu-item" @click="deleteConfig">
                <i class="fas fa-trash"></i> Excluir
            </button>
            <button class="context-menu-item" @click="closeContextMenu">
                <i class="fas fa-times"></i> Cancelar
            </button>
        </div>
    </div>
</template>

<script setup>
import {onMounted, ref, watch} from "vue";
import 'ol/ol.css'
import 'ol-ext/dist/ol-ext.css'
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons-vue";
import { notification } from "ant-design-vue";
import LayerGroup from 'ol/layer/Group';

const userLayersConfig = ref([])
const props = defineProps(['map', 'layerSwitcherData']);
const emit = defineEmits(['draw-start', 'draw-end', 'apply-layer-config']);

const showControls = ref(false)
const activeConfigId = ref('default')
const defaultConfig = ref({ params: {}, selectedModule: 'Padrão', base_layer: 'g_road_wo_building' })
const defaultCaptured = ref(false)
const showSaveInput = ref(false)
const saveConfigName = ref('')
const saveConfigDescription = ref('')
const saveInputRef = ref(null)
const contextMenu = ref({ visible: false, x: 0, y: 0, configId: null })

const getAllLayersRecursive = (layers) => {
    const allLayers = [];
    const layersArray = layers.getArray ? layers.getArray() : layers;

    layersArray.forEach(layer => {
        if (layer instanceof LayerGroup) {
            allLayers.push(layer);
            allLayers.push(...getAllLayersRecursive(layer.getLayers()));
        } else {
            allLayers.push(layer);
        }
    });

    return allLayers;
}

const makeGetOrderKey = (orderIndex) => {
    const cache = new WeakMap();

    const getKey = (layer) => {
        if (!layer) return Number.POSITIVE_INFINITY;

        if (cache.has(layer)) return cache.get(layer);

        const id = layer?.get?.('id');
        if (id != null && orderIndex.has(id)) {
            const key = orderIndex.get(id);
            cache.set(layer, key);
            return key;
        }

        if (layer && typeof layer.getLayers === 'function') {
            const children = layer.getLayers().getArray();
            let min = Number.POSITIVE_INFINITY;

            for (const child of children) {
                const k = getKey(child);
                if (k < min) min = k;
            }

            cache.set(layer, min);
            return min;
        }

        cache.set(layer, Number.POSITIVE_INFINITY);
        return Number.POSITIVE_INFINITY;
    };

    return getKey;
};

const reorderLayerCollectionByParams = (collection, getOrderKey) => {
    const original = collection.getArray().slice();

    const sorted = original
        .map((layer, originalPos) => ({ layer, originalPos }))
        .sort((a, b) => {
            const ka = getOrderKey(a.layer);
            const kb = getOrderKey(b.layer);

            if (ka !== kb) return ka - kb;

            return a.originalPos - b.originalPos;
        })
        .map(x => x.layer);

    while (collection.getLength() > 0) collection.removeAt(0);
    sorted.forEach(layer => collection.push(layer));
};

const reorderGroupsRecursiveByParams = (layersCollection, getOrderKey) => {
    reorderLayerCollectionByParams(layersCollection, getOrderKey);

    layersCollection.getArray().forEach(layer => {
        if (layer && typeof layer.getLayers === 'function') {
            reorderGroupsRecursiveByParams(layer.getLayers(), getOrderKey);
        }
    });
};

const applyParamsRecursive = (layersCollection, paramsById) => {
    layersCollection.getArray().forEach(layer => {
        const id = layer?.get?.('id');
        if (id != null) {
            const param = paramsById.get(id);
            if (param) {
                if (param.visible !== undefined) layer.setVisible(param.visible);
                if (param.opacity !== undefined) layer.setOpacity(param.opacity);
            }
        }

        if (layer instanceof LayerGroup || (layer && typeof layer.getLayers === 'function')) {
            applyParamsRecursive(layer.getLayers(), paramsById);
        }
    });
};

const getUserLayersConfig = async () => {
    const response = await window.axios.get('api/configurations/users/user-layers-config');
    return response.data.data;
}

const cloneConfigParams = (params) => {
    if (!params || typeof params !== 'object') return {}
    return JSON.parse(JSON.stringify(params))
}

const applyLayersConfig = (config, id) => {
    const layerConfig = config?.value || config;
    if (!props.map || !layerConfig) return;
    activeConfigId.value = id;

    emit('apply-layer-config', {
        moduleSelected: layerConfig.selected_module || layerConfig.selectedModule || 'Padrão',
        modules: cloneConfigParams(layerConfig.params),
        updateKey: Date.now(),
        baseLayer: layerConfig.base_layer,
        rasterLayersSelected: layerConfig.raster_layers_selected || layerConfig.rasterLayersSelected || layerConfig.params?.rasterConfig?.selectedIds || layerConfig.params?.__rasterConfig?.selectedIds || [],
    })
}

// Captura o estado atual das layers do mapa
const captureCurrentState = () => {
    if (!props.map) return [];

    const allLayers = getAllLayersRecursive(props.map.getLayers());
    const currentState = [];

    allLayers.forEach(layer => {
        const layerId = layer.get('id');
        if (layerId) {
            currentState.push({
                id: layerId,
                visible: layer.getVisible(),
                opacity: layer.getOpacity()
            });
        }
    });

    return currentState;
}

const getTabTitle = (config) => {
    if (config.description) {
        return `${config.name}\n${config.description}`;
    }
    return config.name;
}

const handleSaveClick = () => {
    if (activeConfigId.value === 'default') {
        showSaveInput.value = !showSaveInput.value;
        if (showSaveInput.value) {
            saveConfigName.value = '';
            saveConfigDescription.value = '';
            setTimeout(() => {
                saveInputRef.value?.focus();
                adjustFormPosition();
            }, 100);
        }
    } else {
        const currentConfig = userLayersConfig.value.find(c => c.id === activeConfigId.value);
        if (currentConfig) {
            saveConfigName.value = currentConfig.name || '';
            saveConfigDescription.value = currentConfig.description || '';
            showSaveInput.value = true;
            setTimeout(() => {
                saveInputRef.value?.focus();
                adjustFormPosition();
            }, 100);
        }
    }
}

const adjustFormPosition = () => {
    if (!showSaveInput.value) return;
    
    setTimeout(() => {
        const saveContainer = document.querySelector('.save-container');
        const saveInputContainer = document.querySelector('.save-input-container');
        if (!saveContainer || !saveInputContainer) return;

        const mapElement = document.getElementById('map');
        if (!mapElement) return;
        
        const mapRect = mapElement.getBoundingClientRect();
        
        saveInputContainer.style.left = 'auto';
        saveInputContainer.style.right = '0';
        
        const newFormRect = saveInputContainer.getBoundingClientRect();
        if (newFormRect.right > mapRect.right) {
            const overflow = newFormRect.right - mapRect.right;
            saveInputContainer.style.right = `${overflow + 8}px`;
        }
        
        const finalFormRect = saveInputContainer.getBoundingClientRect();
        if (finalFormRect.left < mapRect.left) {
            saveInputContainer.style.left = '8px';
            saveInputContainer.style.right = 'auto';
        }
        
        if (finalFormRect.top < mapRect.top) {
            saveInputContainer.style.bottom = 'auto';
            saveInputContainer.style.top = '100%';
            saveInputContainer.style.marginBottom = '0';
            saveInputContainer.style.marginTop = '8px';
        } else {
            saveInputContainer.style.bottom = '100%';
            saveInputContainer.style.top = 'auto';
            saveInputContainer.style.marginBottom = '8px';
            saveInputContainer.style.marginTop = '0';
        }
        
        const finalCheckRect = saveInputContainer.getBoundingClientRect();
        if (finalCheckRect.right > mapRect.right) {
            const overflow = finalCheckRect.right - mapRect.right;
            const currentRight = parseInt(saveInputContainer.style.right) || 0;
            saveInputContainer.style.right = `${currentRight + overflow + 8}px`;
        }
    }, 50);
}

function makeParamsFromModules(modules) {
    return Object.fromEntries(
        Object.entries(modules).map(([moduleName, layers]) => [
            moduleName,
            layers.map(layer => {
                return {
                    id: layer.get?.('id') ?? layer.id,
                    visible: layer.getVisible?.() ?? layer.visible,
                    opacity: layer.getOpacity?.() ?? layer.opacity,
                    zIndex: layer.getZIndex?.() ?? layer.zIndex,
                }
            })
        ])
    )
}

function makeParams() {
    const modules = props.layerSwitcherData.modules

    const params = Object.fromEntries(
        Object.entries(modules).map(([moduleName, layers]) => [
            moduleName,
            layers.map(layer => {
                return {
                    id: layer.get?.('id') ?? layer.id,
                    visible: layer.getVisible?.() ?? layer.visible ?? layer.values_?.visible,
                    opacity: layer.getOpacity?.() ?? layer.opacity ?? layer.state_?.opacity,
                    zIndex: layer.getZIndex?.() ?? layer.zIndex ?? layer.state_?.zIndex,
                }
            })
        ])
    )

    if (Object.keys(props.layerSwitcherData.moduleViewOrders || {}).length) {
        params.moduleViewOrders = props.layerSwitcherData.moduleViewOrders
    }

    params.rasterConfig = { selectedIds: getRasterLayersSelected(), }

    return params
}

function getRasterLayersSelected() {
    return Array.isArray(props.layerSwitcherData?.rasterLayersSelected)
        ? [...props.layerSwitcherData.rasterLayersSelected]
        : []
}

const saveCurrentConfig = async () => {
    if (!saveConfigName.value.trim()) {
        notification.warning({
            message: 'Atenção',
            description: 'Por favor, informe um nome para a visualização.'
        });
        return;
    }

    try {
        if (activeConfigId.value === 'default') {
            await window.axios.post('api/configurations/users/user-layers-config', {
                name: saveConfigName.value.trim(),
                description: saveConfigDescription.value.trim() || null,
                selected_module: props.layerSwitcherData.moduleSelected,
                params: makeParams(),
                base_layer: props.map.getLayers().getArray()[0].get('baseLayerId'),
            });

            notification.success({
                message: 'Sucesso',
                description: 'Visualização salva com sucesso!'
            });
        } else {
            await window.axios.put(`api/configurations/users/user-layers-config/${activeConfigId.value}`, {
                name: saveConfigName.value.trim(),
                description: saveConfigDescription.value.trim() || null,
                selected_module: props.layerSwitcherData.moduleSelected,
                params: makeParams(),
                base_layer: props.map.getLayers().getArray()[0].get('baseLayerId') ?? null,
            });

            notification.success({
                message: 'Sucesso',
                description: 'Visualização alterada com sucesso!'
            });
        }

        userLayersConfig.value = await getUserLayersConfig();

        showSaveInput.value = false;
        saveConfigName.value = '';
        saveConfigDescription.value = '';
    } catch (error) {
        console.error('Erro ao salvar configuração:', error);
        notification.error({
            message: 'Erro',
            description: error.response?.data?.message || 'Erro ao salvar a visualização. Tente novamente.'
        });
    }
}

const cancelSave = () => {
    showSaveInput.value = false;
    saveConfigName.value = '';
    saveConfigDescription.value = '';
}

const showContextMenu = (event, config) => {
    if (config.id === 'default') return;
    
    const menuWidth = 100;
    const menuHeight = 70;
    const buttonRect = event.currentTarget.getBoundingClientRect();
    
    let x = buttonRect.left;
    let y = buttonRect.top - menuHeight - 5;
    
    if (x + menuWidth > window.innerWidth) {
        x = window.innerWidth - menuWidth - 10;
    }
    
    if (y < 10) {
        y = buttonRect.bottom + 5;
    }
    
    contextMenu.value = {
        visible: true,
        x: x,
        y: y,
        configId: config.id
    };
}

const closeContextMenu = () => {
    contextMenu.value = { visible: false, x: 0, y: 0, configId: null     };
}

const deleteConfig = async () => {
    if (!contextMenu.value.configId) return;

    try {
        await window.axios.delete(`api/configurations/users/user-layers-config/${contextMenu.value.configId}`);
        
        notification.success({
            message: 'Sucesso',
            description: 'Visualização excluída com sucesso!'
        });
        
        userLayersConfig.value = await getUserLayersConfig();
        
        if (activeConfigId.value === contextMenu.value.configId) {
            activeConfigId.value = 'default';
            applyLayersConfig(defaultConfig, 'default')
        }
        
        closeContextMenu();
    } catch (error) {
        console.error('Erro ao excluir configuração:', error);
        notification.error({
            message: 'Erro',
            description: error.response?.data?.message || 'Erro ao excluir a visualização. Tente novamente.'
        });
        closeContextMenu();
    }
}

watch(() => showSaveInput.value, (isVisible) => {
    if (isVisible) {
        setTimeout(() => {
            adjustFormPosition();
        }, 150);
    }
});

watch(
    () => props.layerSwitcherData,
    (value) => {
        if (defaultCaptured.value) return
        if (!value?.modules) return
        if (Array.isArray(value.modules)) return

        defaultConfig.value = {
            selectedModule: value.moduleSelected || 'Padrão',
            params: {
                ...makeParamsFromModules(value.modules),
                rasterConfig: {
                    selectedIds: Array.isArray(value.rasterLayersSelected) ? [...value.rasterLayersSelected] : [],
                },
            },
            base_layer: props.map.getLayers().getArray()[0].get('baseLayerId'),
            rasterLayersSelected: Array.isArray(value.rasterLayersSelected) ? [...value.rasterLayersSelected] : [],
        }

        defaultCaptured.value = true
    },
    { immediate: true }
)

onMounted(async () => {
    userLayersConfig.value = await getUserLayersConfig();
    
    document.addEventListener('click', (e) => {
        if (!e.target.closest('.context-menu')) {
            contextMenu.value = { visible: false, x: 0, y: 0, configId: null };
        }
        
        if (showSaveInput.value) {
            const saveContainer = document.querySelector('.save-container');
            const saveInputContainer = document.querySelector('.save-input-container');
            const saveBtn = document.querySelector('.save-btn');
            
            if (saveContainer && saveInputContainer && saveBtn) {
                const clickedInside = saveContainer.contains(e.target) || 
                                     saveInputContainer.contains(e.target) ||
                                     saveBtn.contains(e.target);
                
                if (!clickedInside) {
                    cancelSave();
                }
            }
        }
    });
    
    window.addEventListener('resize', () => {
        if (showSaveInput.value) {
            adjustFormPosition();
        }
    });
})
</script>

<style scoped>
.map-icons {
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    overflow: visible;
}

.map-icon-btn {
    width: var(--home-map-icon-size, 32px);
    height: var(--home-map-icon-size, 32px);
    border-radius: var(--home-map-icon-radius, 6px);
    background-color: rgba(0, 60, 136, .6);
    color: white;
    border: 2px solid white;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.map-icon-btn:hover {
    background-color: rgba(0, 60, 136, .7);
}

.tabs-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    background-color: transparent;
    border-radius: 6px;
    padding: 0;
    height: var(--home-map-icon-size, 32px);
    box-sizing: border-box;
    max-width: calc(100vw - 100px);
    overflow: visible;
}

.save-container {
    position: relative;
    flex-shrink: 0;
    margin: 0 4px;
    overflow: visible;
}

.save-btn {
    flex-shrink: 0;
}

.map-icon-btn i,
.map-icon-btn :deep(.anticon) {
    font-size: var(--home-map-icon-glyph-size, 16px);
}

.save-input-container {
    position: absolute;
    bottom: 100%;
    right: 0;
    left: auto;
    margin-bottom: 8px;
    background-color: rgba(0, 60, 136, .9);
    border: 2px solid white;
    border-radius: 6px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 250px;
    max-width: calc(100vw - 20px);
    z-index: 1000;
    box-sizing: border-box;
}

.save-input {
    padding: 6px 10px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
    font-size: 13px;
    font-family: Arial, sans-serif;
    outline: none;
    width: 100%;
    box-sizing: border-box;
}

.input-with-counter .save-input {
    padding-right: 50px;
}

.save-input::placeholder {
    color: rgba(255, 255, 255, 0.6);
}

.save-input:focus {
    border-color: white;
    background-color: rgba(255, 255, 255, 0.2);
}

.input-with-counter {
    position: relative;
    width: 100%;
}

.char-counter {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
    pointer-events: none;
    font-family: Arial, sans-serif;
    background-color: transparent;
}

.save-input-buttons {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
}

.save-input-btn {
    padding: 6px 16px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    font-family: Arial, sans-serif;
    transition: all 0.2s ease;
}

.save-btn-confirm {
    background-color: rgba(0, 60, 136, .8);
    color: white;
}

.save-btn-confirm:hover {
    background-color: rgba(0, 60, 136, 1);
    border-color: white;
}

.save-btn-cancel {
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
}

.save-btn-cancel:hover {
    background-color: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
}

.expand-up-enter-active,
.expand-up-leave-active {
    transition: all 0.3s ease;
    transform-origin: bottom center;
}

.expand-up-enter-from {
    opacity: 0;
    transform: translateY(10px) scaleY(0);
}

.expand-up-enter-to {
    opacity: 1;
    transform: translateY(0) scaleY(1);
}

.expand-up-leave-from {
    opacity: 1;
    transform: translateY(0) scaleY(1);
}

.expand-up-leave-to {
    opacity: 0;
    transform: translateY(10px) scaleY(0);
}

.tabs-wrapper {
    display: flex;
    flex-direction: row;
    gap: 4px;
    flex-wrap: wrap;
    align-items: center;
    padding: 0 4px;
}

.tab-button {
    background-color: rgba(0, 60, 136, .6);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: var(--home-map-icon-radius, 6px);
    height: var(--home-map-icon-size, 32px);
    min-height: var(--home-map-icon-size, 32px);
    padding: 0 12px;
    cursor: pointer;
    font-size: 12px;
    font-family: Arial, sans-serif;
    text-align: center;
    transition: all 0.2s ease;
    white-space: nowrap;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    line-height: 1;
    margin: 0;
}

.tab-button:hover {
    background-color: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
}

.tab-button.tab-active {
    background-color: rgba(0, 60, 136, .8);
    border-color: white;
    border-width: 2px;
    font-weight: bold;
    height: var(--home-map-icon-size, 32px);
    min-height: var(--home-map-icon-size, 32px);
    box-sizing: border-box;
}

.tab-button.tab-active:hover {
    background-color: rgba(0, 60, 136, .9);
}

.coordinate-display {
    background: #00000090;
    color: #FFFFFF;
    padding: 5px 10px;
    border-radius: 5px;
    font-family: Arial, sans-serif;
    font-size: 14px;
    z-index: 1000;
}

#north-arrow {
    width: 100%;
    border-radius: 50%;
    overflow: hidden;
}

.centered {
    width: 30%;
    display: none;
    position: fixed;
    user-select: none;
    z-index: 1001;
    transform-origin: center center;
    cursor: grab;
    bottom: 50%;
    left: 50%;
    transform: translate(-50%, 50%);
}

.context-menu {
    position: fixed;
    background-color: rgba(0, 60, 136, .95);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 4px;
    padding: 2px;
    z-index: 10000;
    min-width: 100px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.context-menu-item {
    width: 100%;
    padding: 6px 10px;
    background-color: transparent;
    color: white;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-size: 12px;
    font-family: Arial, sans-serif;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.15s ease;
}

.context-menu-item:hover {
    background-color: rgba(255, 255, 255, 0.15);
}

.context-menu-item i {
    width: 14px;
    text-align: center;
    font-size: 11px;
    opacity: 0.9;
}
</style>
